import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import RelationshipForm from './RelationshipForm'
import EditRelationshipForm from './EditRelationshipForm'
import DeleteConfirmation from './DeleteConfirmation'
import { getBaseRelationshipType, getRelationshipBasedOnSex } from '../utils/relationshipMappings'
import { differenceInYears, parseISO, format } from 'date-fns'
import { useTheme } from '../context/ThemeContext'

const { FiX, FiPlus, FiTrash2, FiEdit, FiCheckCircle, FiUser, FiHome, FiUsers, FiCalendar, FiMapPin, FiFacebook, FiLinkedin, FiTwitter, FiInstagram, FiBriefcase, FiHeart, FiSun, FiMoon, FiFilter, FiCheck } = FiIcons

const getRelationshipWeight = (baseType, isFamily) => {
  if (!isFamily) return 1000;
  const weights = {
    'spouse': 1, 'child': 2, 'parent': 3, 'sibling': 4, 'step_child': 5,
    'step_parent': 6, 'step_sibling': 7, 'half_sibling': 8, 'grandparent': 9,
    'grandchild': 10, 'sibling_child': 11, 'parental_sibling': 12,
    'parent_in_law': 13, 'child_in_law': 14, 'sibling_in_law': 15,
    'cousin': 16, 'parental_sibling_in_law': 17, 'sibling_child_in_law': 18,
    'cousin_in_law': 19, 'great_grandparent': 20, 'great_grandchild': 21,
    'grandparent_sibling': 22, 'grandsibling_child': 23, 'cousin_once_removed': 24,
    'second_cousin': 25, 'second_cousin_once_removed': 26, 'third_cousin': 27
  };
  return weights[baseType] || 99;
};

const FullscreenPersonCard = ({
  person,
  people,
  allRelationships,
  initialEditingRelationship = null,
  initialShowAddForm = false,
  onClose,
  onNavigate,
  onAddPerson,
  onAddRelationships,
  onUpdateRelationship,
  onDeleteRelationship,
  onEditPerson,
  onDeletePerson
}) => {
  const [showForm, setShowForm] = useState(initialShowAddForm)
  const [editingRelationship, setEditingRelationship] = useState(initialEditingRelationship)
  const [relationships, setRelationships] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [smartResult, setSmartResult] = useState(null)
  const [relToDelete, setRelToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeFilters, setActiveFilters] = useState(['family', 'friends', 'business', 'other'])
  const { theme, toggleTheme } = useTheme()
  const scrollContainerRef = useRef(null)

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0)
    }
  }, [person?.id])

  useEffect(() => {
    if (person && allRelationships) {
      setRelationships(allRelationships.filter(r => r.person_a_id === person.id || r.person_b_id === person.id))
      setLoading(false)
    }
  }, [person, allRelationships])

  const calculateAge = (dob) => {
    if (!dob) return null
    try {
      return differenceInYears(new Date(), parseISO(dob))
    } catch (e) {
      return null
    }
  }

  const formatBirthday = (dob) => {
    if (!dob) return null
    try {
      return format(parseISO(dob), 'MMMM d, yyyy')
    } catch (e) {
      return dob
    }
  }

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed.startsWith('http')) return `https://${trimmed}`;
    return trimmed;
  }

  const handleAddRels = async (data) => {
    try {
      setLoading(true)
      const res = await onAddRelationships(person.id, data)
      if (res?.addedCount > 0) {
        setSmartResult(res)
        setTimeout(() => setSmartResult(null), 5000)
      }
      setShowForm(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEditRel = async (data) => {
    try {
      setLoading(true)
      const res = await onUpdateRelationship(editingRelationship.id, data, person.id, people)
      if (res?.addedCount > 0) {
        setSmartResult(res)
        setTimeout(() => setSmartResult(null), 5000)
      }
      setEditingRelationship(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleFilter = (filter) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter))
    } else {
      setActiveFilters([...activeFilters, filter])
    }
  }

  const getDisplayRels = () => {
    const allMapped = relationships.map(rel => {
      const isA = rel.person_a_id === person.id
      const otherId = isA ? rel.person_b_id : rel.person_a_id
      const other = people.find(p => p.id === otherId)
      if (!other) return null;
      const type = isA ? rel.relationship_type_b : rel.relationship_type
      const baseType = getBaseRelationshipType(type)
      
      let category = 'other'
      const familyTypes = [
        'parent', 'child', 'sibling', 'spouse', 'grandparent', 'grandchild', 'parental_sibling', 'sibling_child', 'cousin', 'parent_in_law', 'child_in_law', 'sibling_in_law', 'step_parent', 'step_child', 'step_sibling', 'half_sibling', 'ex_spouse', 'grandparent_sibling', 'grandsibling_child', 'sibling_child_in_law', 'parental_sibling_in_law', 'cousin_in_law', 'grandsibling_child_in_law', 'grandparent_sibling_in_law', 'great_grandparent', 'great_grandchild', 'cousin_once_removed', 'second_cousin', 'second_cousin_once_removed', 'third_cousin'
      ];
      const businessTypes = ['colleague', 'boss', 'employee'];
      
      if (familyTypes.includes(baseType)) {
        category = 'family'
      } else if (baseType === 'friend') {
        category = 'friends'
      } else if (businessTypes.includes(baseType)) {
        category = 'business'
      }

      return {
        id: rel.id,
        name: other.name,
        type: getRelationshipBasedOnSex(baseType, other.sex) || type,
        baseType,
        category,
        personId: otherId,
        photo_url: other.photo_url,
        sex: other.sex,
        is_family: category === 'family',
        nicknames: other.nicknames,
        notes: other.notes
      }
    }).filter(Boolean);

    return allMapped
      .filter(rel => activeFilters.includes(rel.category))
      .sort((a, b) => {
        const weightA = getRelationshipWeight(a.baseType, a.is_family);
        const weightB = getRelationshipWeight(b.baseType, b.is_family);
        if (weightA !== weightB) return weightA - weightB;
        return a.name.localeCompare(b.name);
      });
  }

  const age = calculateAge(person.dob)
  const birthday = formatBirthday(person.dob)
  const displayRels = getDisplayRels()

  const filterOptions = [
    { id: 'family', label: 'Family', icon: FiHome, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/30' },
    { id: 'friends', label: 'Friends', icon: FiHeart, color: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-50 dark:bg-pink-900/30' },
    { id: 'business', label: 'Business', icon: FiBriefcase, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30' },
    { id: 'other', label: 'Other', icon: FiUsers, color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-slate-800' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex flex-col overflow-hidden"
    >
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 text-gray-900 dark:text-gray-100 transition-colors duration-300">
        
        {/* Compact Sticky Header */}
        <div className="bg-gradient-to-r from-blue-100/90 to-indigo-100/90 dark:from-slate-900/90 dark:to-slate-800/90 backdrop-blur-md border-b border-indigo-200/50 dark:border-slate-700/50 px-4 py-3 sticky top-0 z-20 shadow-sm transition-colors duration-300">
          <div className="flex items-start">
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/60 dark:hover:bg-slate-800/60 rounded-full transition-colors shrink-0 mt-0.5 mr-2 -ml-2"
            >
              <SafeIcon icon={FiX} size={22} className="text-gray-700 dark:text-gray-300" />
            </button>
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-white dark:border-slate-700 shadow-sm bg-white/50 dark:bg-slate-800/50 shrink-0 mr-3">
                  {person.photo_url ? (
                    <img src={person.photo_url} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-indigo-300 dark:text-slate-600" size={24} />
                    </div>
                  )}
                </div>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white break-words leading-tight mt-1 sm:mt-2">
                  {person.name}
                </h1>
              </div>
              <div className="flex items-center mt-3 space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] sm:text-[11px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">
                    {person.proximity || 'Medium'} Proximity
                  </span>
                  {person.is_family && <span className="text-[10px] sm:text-[11px] text-pink-600 dark:text-pink-400 font-bold">• Family</span>}
                </div>
                <div className="flex items-center space-x-1 shrink-0 bg-white/40 dark:bg-slate-800/40 rounded-lg p-0.5 border border-white/50 dark:border-slate-700/50">
                  <button onClick={toggleTheme} className="p-1.5 text-indigo-600 dark:text-yellow-400 hover:bg-white/80 dark:hover:bg-slate-700/80 rounded-md transition-colors" title="Toggle Theme">
                    <SafeIcon icon={theme === 'light' ? FiMoon : FiSun} size={16} />
                  </button>
                  <button 
                    onClick={() => onEditPerson(person)} 
                    className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-white/80 dark:hover:bg-slate-700/80 rounded-md transition-colors"
                  >
                    <SafeIcon icon={FiEdit} size={16} />
                  </button>
                  <button onClick={() => {onDeletePerson(person); onClose();}} className="p-1.5 text-red-600 dark:text-red-400 hover:bg-white/80 dark:hover:bg-slate-700/80 rounded-md transition-colors">
                    <SafeIcon icon={FiTrash2} size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pb-20">
          <div className="max-w-4xl mx-auto px-4 py-6">
            
            {/* Detailed Info Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="md:col-span-1">
                <div className="relative w-full aspect-square rounded-3xl overflow-hidden shadow-lg border-4 border-white dark:border-slate-800 bg-white/50 dark:bg-slate-800/50 transition-colors duration-300">
                  {person.photo_url ? (
                    <img src={person.photo_url} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-indigo-200 dark:text-slate-600" size={60} />
                    </div>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-white dark:bg-slate-800 text-[11px] font-bold uppercase tracking-wider border border-gray-100 dark:border-slate-700 shadow-sm transition-colors duration-300">
                    {person.sex === 'male' ? '♂️ Male' : person.sex === 'female' ? '♀️ Female' : '⚧️ Other'}
                  </span>
                  {person.is_family && (
                    <span className="px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold uppercase tracking-wider border border-indigo-100 dark:border-indigo-800 shadow-sm flex items-center transition-colors duration-300">
                      <SafeIcon icon={FiHome} className="mr-1.5" size={12} /> Family
                    </span>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 space-y-4">
                {person.nicknames && (
                  <div>
                    <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-bold uppercase tracking-widest mb-1">Also Known As</p>
                    <p className="text-xl text-gray-700 dark:text-gray-200 font-medium italic">"{person.nicknames}"</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {birthday && (
                    <div className="flex items-center text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 p-3 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm transition-colors duration-300">
                      <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded-xl mr-3 transition-colors duration-300">
                        <SafeIcon icon={FiCalendar} className="text-blue-500 dark:text-blue-400" size={18} />
                      </div>
                      <div>
                        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-bold uppercase">Birthday</p>
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{birthday}</p>
                        {age !== null && <p className="text-[10px] text-gray-500 dark:text-gray-500 font-medium">{age} years old</p>}
                      </div>
                    </div>
                  )}
                  {person.proximity && (
                    <div className="flex items-center text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 p-3 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm transition-colors duration-300">
                      <div className="bg-green-50 dark:bg-green-900/30 p-2 rounded-xl mr-3 transition-colors duration-300">
                        <SafeIcon icon={FiMapPin} className="text-green-500 dark:text-green-400" size={18} />
                      </div>
                      <div>
                        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-bold uppercase">Proximity</p>
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{person.proximity}</p>
                      </div>
                    </div>
                  )}
                </div>

                {person.notes && (
                  <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm transition-colors duration-300">
                    <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-bold uppercase tracking-widest mb-2 flex items-center">
                      <SafeIcon icon={FiEdit} className="mr-1.5" size={12} /> Notes
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">{person.notes}</p>
                  </div>
                )}

                {/* Secondary Notes (from connections) */}
                {relationships.some(rel => {
                  const other = people.find(p => p.id === (rel.person_a_id === person.id ? rel.person_b_id : rel.person_a_id));
                  return other?.notes;
                }) && (
                  <div className="space-y-3">
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-[0.2em] mt-6 mb-2">Connected Notes</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {relationships.map(rel => {
                        const isA = rel.person_a_id === person.id
                        const otherId = isA ? rel.person_b_id : rel.person_a_id
                        const other = people.find(p => p.id === otherId)
                        if (!other?.notes) return null;
                        
                        const type = isA ? rel.relationship_type_b : rel.relationship_type
                        const baseType = getBaseRelationshipType(type)
                        const finalType = getRelationshipBasedOnSex(baseType, other.sex) || type

                        return (
                          <div key={`sec-note-${rel.id}`} className="bg-indigo-50/60 dark:bg-indigo-900/20 p-3 rounded-2xl border border-indigo-100/60 dark:border-indigo-800/50 relative overflow-hidden transition-colors duration-300">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-300 dark:bg-indigo-600"></div>
                            <p className="text-[9px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1.5 flex items-center">
                              <SafeIcon icon={FiUsers} className="mr-1.5" size={10} />
                              {other.name}'s Note ({finalType})
                            </p>
                            <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed italic">"{other.notes}"</p>
                          </div>
                        )
                      }).filter(Boolean)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Social Links */}
            {(person.facebook_url || person.linkedin_url || person.instagram_url || person.twitter_url) && (
              <div className="mb-10">
                <h2 className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4">Social Presence</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {person.facebook_url && (
                    <a href={normalizeUrl(person.facebook_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 transition-all group">
                      <SafeIcon icon={FiFacebook} className="text-blue-600 dark:text-blue-400 mr-2.5 group-hover:scale-110 transition-transform" size={18} />
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Facebook</span>
                    </a>
                  )}
                  {person.linkedin_url && (
                    <a href={normalizeUrl(person.linkedin_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 transition-all group">
                      <SafeIcon icon={FiLinkedin} className="text-blue-700 dark:text-blue-500 mr-2.5 group-hover:scale-110 transition-transform" size={18} />
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">LinkedIn</span>
                    </a>
                  )}
                  {person.instagram_url && (
                    <a href={normalizeUrl(person.instagram_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white dark:bg-slate-800 hover:bg-pink-50 dark:hover:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 transition-all group">
                      <SafeIcon icon={FiInstagram} className="text-pink-600 dark:text-pink-400 mr-2.5 group-hover:scale-110 transition-transform" size={18} />
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Instagram</span>
                    </a>
                  )}
                  {person.twitter_url && (
                    <a href={normalizeUrl(person.twitter_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 transition-all group">
                      <SafeIcon icon={FiTwitter} className="text-gray-800 dark:text-gray-400 mr-2.5 group-hover:scale-110 transition-transform" size={18} />
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Twitter</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Relationships Section */}
            <div className="border-t border-gray-100 dark:border-slate-800 pt-8">
              <div className="flex flex-col space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">Connections ({displayRels.length})</h2>
                  <button 
                    onClick={() => setShowForm(true)} 
                    className="flex items-center space-x-1.5 text-blue-600 dark:text-blue-400 text-xs font-black hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full"
                  >
                    <SafeIcon icon={FiPlus} size={14} />
                    <span>ADD NEW</span>
                  </button>
                </div>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                  {filterOptions.map(option => (
                    <button
                      key={option.id}
                      onClick={() => toggleFilter(option.id)}
                      className={`flex items-center space-x-2 px-3 py-1.5 rounded-xl border transition-all ${activeFilters.includes(option.id) ? `${option.bg} border-transparent shadow-sm scale-[1.02]` : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-800 text-gray-400 dark:text-gray-600 grayscale'}`}
                    >
                      <div className={`flex items-center justify-center w-4 h-4 rounded border ${activeFilters.includes(option.id) ? 'bg-current border-transparent' : 'border-gray-200 dark:border-slate-700'}`}>
                        {activeFilters.includes(option.id) && <SafeIcon icon={FiCheck} size={10} className="text-white" />}
                      </div>
                      <SafeIcon icon={option.icon} size={14} className={activeFilters.includes(option.id) ? option.color : ''} />
                      <span className={`text-xs font-bold ${activeFilters.includes(option.id) ? 'text-gray-800 dark:text-gray-200' : ''}`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {smartResult && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-2xl flex items-center space-x-3 transition-colors duration-300">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 dark:text-green-400" size={20} />
                  <p className="text-sm font-bold text-green-800 dark:text-green-300">{smartResult.message || `Added ${smartResult.addedCount} smart connections! ✨`}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                {displayRels.map(rel => (
                  <div key={rel.id} className="bg-gray-50/80 dark:bg-slate-800/80 p-4 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:bg-white dark:hover:bg-slate-800 transition-all group flex items-start justify-between">
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                      <div 
                        className="w-11 h-11 rounded-full overflow-hidden bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity mt-0.5"
                        onClick={() => {
                          const target = people.find(p => p.id === rel.personId);
                          if (target && onNavigate) onNavigate(target);
                        }}
                      >
                        {rel.photo_url ? (
                          <img src={rel.photo_url} alt={rel.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="text-gray-300 dark:text-slate-600" size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <p 
                          className="text-sm font-black text-gray-900 dark:text-white break-words transition-colors duration-300 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          onClick={() => {
                            const target = people.find(p => p.id === rel.personId);
                            if (target && onNavigate) onNavigate(target);
                          }}
                        >
                          {rel.name}
                        </p>
                        {rel.nicknames && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic break-words mt-0.5">
                            "{rel.nicknames}"
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider w-fit border transition-colors duration-300 ${rel.is_family ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800'}`}>
                            {rel.type}
                          </span>
                        </div>
                        {rel.notes && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2 leading-relaxed italic">
                            "{rel.notes}"
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-1 flex-shrink-0 ml-2">
                      <button onClick={() => setEditingRelationship(rel)} className="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg transition-colors">
                        <SafeIcon icon={FiEdit} size={16} />
                      </button>
                      <button onClick={() => setRelToDelete(rel)} className="p-1.5 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-700 rounded-lg transition-colors">
                        <SafeIcon icon={FiTrash2} size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {displayRels.length === 0 && (
                  <div className="col-span-full py-12 text-center bg-white dark:bg-slate-800 rounded-3xl border-2 border-dashed border-gray-100 dark:border-slate-700 transition-colors duration-300">
                    <SafeIcon icon={FiUsers} className="mx-auto text-gray-200 dark:text-slate-600 mb-4" size={40} />
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest">No connections matching filters</p>
                    <button onClick={() => setShowForm(true)} className="mt-3 text-blue-600 dark:text-blue-400 text-xs font-black hover:underline">ADD FIRST CONNECTION</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlays */}
      <AnimatePresence>
        {editingRelationship && (
          <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4">
            <EditRelationshipForm
              relationship={editingRelationship}
              person={person}
              people={people}
              onSubmit={handleEditRel}
              onCancel={() => setEditingRelationship(null)}
            />
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4">
            <RelationshipForm
              person={person}
              people={people}
              existingRelationships={relationships}
              allRelationships={allRelationships}
              onSubmit={handleAddRels}
              onCancel={() => setShowForm(false)}
              onAddNewPerson={onAddPerson}
            />
          </div>
        )}

        {relToDelete && (
          <DeleteConfirmation
            title="Remove Connection?"
            message={`Are you sure you want to remove the connection with ${relToDelete.name}?`}
            loading={isDeleting}
            onConfirm={async () => {
              setIsDeleting(true);
              await onDeleteRelationship(relToDelete.id);
              setRelToDelete(null);
              setIsDeleting(false);
            }}
            onCancel={() => setRelToDelete(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default FullscreenPersonCard